import type { BusinessUnit } from '@/interfaces/countryDefaultHierarchy.interface';
import type { BusinessUnitConfig } from '@/modules/bu/registry.base';
import {
  BusinessUnitKey,
  DEFAULT_DASHBOARD_ROUTE_NAME,
  DEFAULT_INVEST_ROUTE_NAME,
  DEFAULT_LOGIN_ROUTE_NAME,
  DEFAULT_SELECT_ACCOUNT_ROUTE_NAME,
  getBusinessUnitConfig,
} from '@/modules/bu/registry.base';
import { getEffectiveBu, isBusinessUnitDomain, normalizeBusinessUnit } from '@/utils/bu.utils';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';

/**
 * Context options for the BU resolver.
 *
 * Pass whichever data source is available in the current context.
 * The resolver uses a priority cascade:
 *   1. Route / domain / stored BU (always available in-app)
 *   2. Explicit `bu` value (when you already have a BusinessUnit string)
 *   3. Order summary `business_unit` field
 *   4. Plan `business_unit` field
 *   5. Solution `business_unit` field
 */
export interface BusinessUnitResolverOptions {
  /** Explicit BU value — highest priority data-level source. */
  bu?: MaybeRefOrGetter<BusinessUnit | undefined>;
  /** Order summary with optional business_unit field. */
  orderSummary?: MaybeRefOrGetter<{ business_unit?: BusinessUnit } | undefined>;
  /** Plan with optional business_unit field. */
  plan?: MaybeRefOrGetter<{ business_unit?: BusinessUnit } | undefined>;
  /** Solution with optional business_unit field. */
  solution?: MaybeRefOrGetter<{ business_unit?: BusinessUnit } | undefined>;
}

export interface BusinessUnitResolverReturn {
  /** The resolved BU key (from data context or route). */
  current: ComputedRef<BusinessUnitKey | undefined>;
  /** True if the resolved BU is FLOKEE. */
  isFlokee: ComputedRef<boolean>;
  /** True if the resolved BU is CLEEPO. */
  isCleepo: ComputedRef<boolean>;
  /** The BU resolved purely from route path / domain / stored value. */
  routeBu: ComputedRef<BusinessUnitKey | undefined>;
  /** The BU resolved from data context (explicit bu, order summary, or plan). */
  dataBu: ComputedRef<BusinessUnitKey | undefined>;
  /** True if routeBu and dataBu are both present and differ. */
  inConflict: ComputedRef<boolean>;
  /** The full BU config for the resolved BU. */
  config: ComputedRef<BusinessUnitConfig | undefined>;
  /** Dashboard route name for the resolved BU. */
  dashboardRouteName: ComputedRef<string>;
  /** Login route name for the resolved BU. */
  loginRouteName: ComputedRef<string>;
  /** Select-account route name for the resolved BU. */
  selectAccountRouteName: ComputedRef<string>;
  /** Invest route name for the resolved BU. */
  investRouteName: ComputedRef<string>;
  /** Help center URL for the resolved BU. */
  helpCenterUrl: ComputedRef<string | undefined>;
  /** True when current origin is a BU production domain (domain-enforced context). */
  isDomainEnforcedContext: ComputedRef<boolean>;
}

/**
 * Centralized BU resolver composable.
 *
 * Consumers never need to know where the BU comes from or compare
 * against string literals. Pass context (order summary, plan, solution, or
 * explicit BU) and use the reactive computed properties.
 *
 * @example
 * // Route-only resolution (middleware, logout)
 * const { isFlokee, loginRouteName } = useBusinessUnitResolver()
 *
 * @example
 * // With order summary context (store init, enforcement)
 * const { isFlokee, inConflict } = useBusinessUnitResolver({ orderSummary })
 *
 * @example
 * // With plan context (cart, plan views)
 * const { isFlokee } = useBusinessUnitResolver({ plan: currentPlan })
 *
 * @example
 * // With solution context
 * const { isFlokee } = useBusinessUnitResolver({ solution: currentSolution })
 *
 * @example
 * // Explicit BU value (admin views)
 * const { isFlokee } = useBusinessUnitResolver({ bu: order.business_unit })
 */
export const useBusinessUnitResolver = (
  options?: BusinessUnitResolverOptions,
): BusinessUnitResolverReturn => {
  const route = useRoute();

  // --- Route-based resolution (always available) ---
  const routeBu = computed<BusinessUnitKey | undefined>(() =>
    getEffectiveBu({
      path_value: route.path,
      origin_value: window.location.origin,
      use_stored: true,
    }),
  );

  // --- Data-based resolution (from context) ---
  const dataBu = computed<BusinessUnitKey | undefined>(() => {
    // Priority 1: Explicit BU value
    const explicit_bu = toValue(options?.bu);
    if (explicit_bu) return normalizeBusinessUnit(explicit_bu);

    // Priority 2: Order summary
    const summary_bu = toValue(options?.orderSummary)?.business_unit;
    if (summary_bu) return normalizeBusinessUnit(summary_bu);

    // Priority 3: Plan
    const plan_bu = toValue(options?.plan)?.business_unit;
    if (plan_bu) return normalizeBusinessUnit(plan_bu);

    // Priority 4: Solution
    const solution_bu = toValue(options?.solution)?.business_unit;
    if (solution_bu) return normalizeBusinessUnit(solution_bu);

    return undefined;
  });

  // --- Combined resolution: route wins, data is fallback ---
  const current = computed<BusinessUnitKey | undefined>(() => routeBu.value ?? dataBu.value);

  const isFlokee = computed(() => current.value === BusinessUnitKey.Flokee);
  const isCleepo = computed(() => current.value === BusinessUnitKey.Cleepo);

  const inConflict = computed(() => {
    if (!routeBu.value || !dataBu.value) return false;
    return routeBu.value !== dataBu.value;
  });

  const config = computed(() => getBusinessUnitConfig(current.value));

  const dashboardRouteName = computed(
    () => config.value?.dashboardRouteName ?? DEFAULT_DASHBOARD_ROUTE_NAME,
  );

  const loginRouteName = computed(() => config.value?.loginRouteName ?? DEFAULT_LOGIN_ROUTE_NAME);

  const selectAccountRouteName = computed(
    () => config.value?.selectAccountRouteName ?? DEFAULT_SELECT_ACCOUNT_ROUTE_NAME,
  );

  const investRouteName = computed(
    () => config.value?.investRouteName ?? DEFAULT_INVEST_ROUTE_NAME,
  );

  const helpCenterUrl = computed(() => config.value?.helpCenterUrl);
  const isDomainEnforcedContext = computed(() => isBusinessUnitDomain(window.location.origin));

  return {
    current,
    isFlokee,
    isCleepo,
    routeBu,
    dataBu,
    inConflict,
    config,
    dashboardRouteName,
    loginRouteName,
    selectAccountRouteName,
    investRouteName,
    helpCenterUrl,
    isDomainEnforcedContext,
  };
};
